import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Chip,
  Avatar,
  Fade,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GroupsIcon from '@mui/icons-material/Groups';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CreatorEconomyIcon from '@mui/icons-material/Handshake';

// Enhanced styled components with glassmorphism for landing page
const AudienceCard = styled(Card)(({ theme, audience }) => ({
  height: '100%',
  borderRadius: '20px',
  background: audience === 'brand'
    ? 'linear-gradient(135deg, rgba(255, 107, 107, 0.08) 0%, rgba(255, 184, 34, 0.12) 100%)'
    : 'linear-gradient(135deg, rgba(129, 236, 236, 0.08) 0%, rgba(116, 185, 255, 0.12) 100%)',
  backdropFilter: 'blur(12px)',
  border: `2px solid ${audience === 'brand' ? 'rgba(255, 107, 107, 0.25)' : 'rgba(129, 236, 236, 0.25)'}`,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',

  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: `0 20px 60px ${audience === 'brand' ? 'rgba(255, 107, 107, 0.25)' : 'rgba(129, 236, 236, 0.25)'}`,
    background: audience === 'brand'
      ? 'linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 184, 34, 0.18) 100%)'
      : 'linear-gradient(135deg, rgba(129, 236, 236, 0.15) 0%, rgba(116, 185, 255, 0.18) 100%)',
  }
}));

const MetricCard = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  }
}));

const Home = () => {
  const [viewMode, setViewMode] = useState('all'); // 'all', 'brand', 'creator'

  // Enhanced features with audience-specific messaging
  const features = {
    brand: {
      title: 'For Brands',
      subtitle: 'Turn your marketing budget into profit, not just posts',
      description: 'Stop throwing money at influencers with fake followers. Access creators who deliver actual sales, real engagement, and measurable growth for your brand.',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      benefits: [
        'Average 3.2x ROI increase vs traditional advertising',
        'Zero fake followers - all creators are engagement-verified',
        'Track real sales and conversions, not vanity metrics',
        'Money-back guarantee on campaign completion'
      ],
      stats: [
        { number: '3.2x', label: 'Average ROI Increase', icon: <TrendingUpIcon /> },
        { number: '98%', label: 'Campaign Completion', icon: <VerifiedIcon /> },
        { number: '2K+', label: 'Verified Creators', icon: <GroupsIcon /> },
        { number: '48hrs', label: 'Response Time', icon: <AnalyticsIcon /> }
      ],
      ctaPrimary: 'Stop Wasting Ad Budget',
      ctaSecondary: 'See Real ROI Examples'
    },
    creator: {
      title: 'For Creators',
      subtitle: 'Your creativity deserves real money, not just exposure',
      description: 'Tired of brands offering "exposure" as payment? Connect with companies that actually value your work and pay what you deserve. No more chasing invoices.',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      benefits: [
        'Creators earn ₹2,07,500+ monthly (no "exposure" payments)',
        'Guaranteed payment within 48 hours - no chasing invoices',
        'Work with 500+ brands that actually budget for creators',
        'Grow your audience by 150% through quality partnerships'
      ],
      stats: [
        { number: '₹2.07L', label: 'Avg Monthly Earnings', icon: <AttachMoneyIcon /> },
        { number: '48hrs', label: 'Payment Guarantee', icon: <VerifiedIcon /> },
        { number: '500+', label: 'Premium Brands', icon: <GroupsIcon /> },
        { number: '150%', label: 'Follower Growth', icon: <TrendingUpIcon /> }
      ],
      ctaPrimary: 'Get Paid What You\'re Worth',
      ctaSecondary: 'See Creator Success Stories'
    }
  };

  const allFeatures = [
    {
      title: 'For Brands',
      description: 'Find the perfect influencers for your campaigns and track performance metrics in real-time.',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      benefits: [
        'Access to verified influencers',
        'Campaign performance analytics',
        'Streamlined communication',
        'Simplified contracting'
      ]
    },
    {
      title: 'For Creators',
      description: 'Discover brand collaboration opportunities that match your audience and content style.',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      benefits: [
        'Find relevant brand partnerships',
        'Manage all collaborations in one place',
        'Track earnings and performance',
        'Build your professional portfolio'
      ]
    }
  ];

  // Success stories for cross-audience trust building
  const successStories = [
    {
      type: 'brand',
      name: 'TechStartup Inc.',
      result: '400% ROI increase',
      quote: 'InfHire helped us find micro-influencers who delivered authentic engagement and real conversions.',
      avatar: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=150'
    },
    {
      type: 'creator',
      name: '@sarah_lifestyle',
      result: '₹4.32L monthly earnings',
      quote: 'Finally found brands that respect my creative process and pay fairly. Game changer!',
      avatar: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=150'
    }
  ];

  return (
    <>
      {/* Enhanced Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          {/* Universal Hook */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              component="h1"
              variant="h2"
              color="text.primary"
              gutterBottom
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Connect & Collab
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}
            >
              The platform that brings brands and influencers together for impactful collaborations
            </Typography>

            {/* Audience Selector */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              sx={{
                mb: 4,
                '& .MuiToggleButton-root': {
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  mx: 0.5,
                  border: '2px solid',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(12px)',

                  '&.Mui-selected': {
                    background: 'rgba(25, 118, 210, 0.1)',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  }
                }
              }}
            >
              <ToggleButton value="all">Both Audiences</ToggleButton>
              <ToggleButton value="brand">I'm a Brand</ToggleButton>
              <ToggleButton value="creator">I'm a Creator</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Dual Audience Cards */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Fade in timeout={600}>
                <AudienceCard audience="brand">
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <GroupsIcon sx={{ fontSize: 48, color: '#ff6b6b', mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                      For Brands
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                      Turn your marketing budget into profit, not just<br />
                      posts
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
                      Stop wasting money on fake followers. Connect with creators who deliver actual sales and measurable growth for your brand.
                    </Typography>

                    {/* Key metrics for brands */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      <Grid item xs={6}>
                        <Typography variant="h6" sx={{ color: '#ff6b6b', fontWeight: 700 }}>3.2x</Typography>
                        <Typography variant="body2">ROI Increase</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h6" sx={{ color: '#ff6b6b', fontWeight: 700 }}>98%</Typography>
                        <Typography variant="body2">Completion Rate</Typography>
                      </Grid>
                    </Grid>

                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        size="large"
                        component={RouterLink}
                        to="/register"
                        fullWidth
                        sx={{
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #ff5252 0%, #ff9800 100%)'
                          }
                        }}
                      >
                        Stop Wasting Ad Budget
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        component={RouterLink}
                        to="/how-it-works"
                        fullWidth
                        sx={{
                          borderRadius: '12px',
                          borderColor: '#ff6b6b',
                          color: '#ff6b6b',
                          '&:hover': {
                            borderColor: '#ff5252',
                            color: '#ff5252',
                            backgroundColor: 'rgba(255, 107, 107, 0.1)'
                          }
                        }}
                      >
                        See How It Works
                      </Button>
                    </Stack>
                  </CardContent>
                </AudienceCard>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Fade in timeout={600} style={{ transitionDelay: '200ms' }}>
                <AudienceCard audience="creator">
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <CreatorEconomyIcon sx={{ fontSize: 48, color: '#4ecdc4', mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                      For Creators
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                      Your creativity deserves real money, not just exposure
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
                      No more "we'll pay you in exposure." Work with brands that respect your craft and pay what you're actually worth.
                    </Typography>

                    {/* Key metrics for creators */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      <Grid item xs={6}>
                        <Typography variant="h6" sx={{ color: '#4ecdc4', fontWeight: 700 }}>₹2.07L</Typography>
                        <Typography variant="body2">Avg Monthly</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h6" sx={{ color: '#4ecdc4', fontWeight: 700 }}>48hrs</Typography>
                        <Typography variant="body2">Payment Time</Typography>
                      </Grid>
                    </Grid>

                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        size="large"
                        component={RouterLink}
                        to="/register"
                        fullWidth
                        sx={{
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #4ecdc4 0%, #74b9ff 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #00b894 0%, #0984e3 100%)'
                          }
                        }}
                      >
                        Get Paid What You're Worth
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        component={RouterLink}
                        to="/how-it-works"
                        fullWidth
                        sx={{
                          borderRadius: '12px',
                          borderColor: '#4ecdc4',
                          color: '#4ecdc4',
                          '&:hover': {
                            borderColor: '#00b894',
                            color: '#00b894',
                            backgroundColor: 'rgba(78, 205, 196, 0.1)'
                          }
                        }}
                      >
                        Browse Opportunities
                      </Button>
                    </Stack>
                  </CardContent>
                </AudienceCard>
              </Fade>
            </Grid>
          </Grid>

          {/* Universal CTA */}
          <Box sx={{ textAlign: 'center' }}>
            <Chip
              label="500+ brands • 2,000+ creators • ₹16.6Cr+ paid out"
              sx={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                fontSize: '1rem',
                py: 1,
                px: 2
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Dynamic Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            component="h2"
            variant="h3"
            align="center"
            color="text.primary"
            gutterBottom
            sx={{ mb: 2, fontWeight: 600 }}
          >
            {viewMode === 'brand' ? 'Why Brands Choose InfHire' :
             viewMode === 'creator' ? 'Why Creators Love InfHire' : 'How InfHire Works'}
          </Typography>

          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: '600px', mx: 'auto' }}
          >
            {viewMode === 'brand' ? 'Connect with authentic creators who drive real results for your campaigns' :
             viewMode === 'creator' ? 'Join a platform that puts creators first with fair pay and respect' :
             'The platform that brings brands and creators together for authentic partnerships'}
          </Typography>

          <Grid container spacing={4}>
            {/* Show audience-specific features or both */}
            {viewMode === 'all' ? (
              allFeatures.map((feature, index) => (
                <Grid item key={feature.title} xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '16px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={feature.image}
                      alt={feature.title}
                      sx={{ borderRadius: '16px 16px 0 0' }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography gutterBottom variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                        {feature.description}
                      </Typography>
                      <Box>
                        {feature.benefits.map((benefit) => (
                          <Box key={benefit} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <CheckCircleOutlineIcon color="primary" sx={{ mr: 2 }} fontSize="small" />
                            <Typography variant="body2">{benefit}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              /* Single audience detailed view */
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  {/* Feature details */}
                  <Grid item xs={12} md={6}>
                    <Box
                      component="img"
                      src={features[viewMode]?.image}
                      alt={features[viewMode]?.title}
                      sx={{
                        width: '100%',
                        borderRadius: '20px',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ pl: { md: 4 } }}>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
                        {features[viewMode]?.subtitle}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {features[viewMode]?.description}
                      </Typography>

                      {/* Benefits with enhanced styling */}
                      <Box sx={{ mb: 4 }}>
                        {features[viewMode]?.benefits.map((benefit, index) => (
                          <Fade in timeout={600} style={{ transitionDelay: `${index * 100}ms` }} key={benefit}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                              <Box
                                sx={{
                                  background: viewMode === 'brand' ? 'linear-gradient(135deg, #1976d2, #1565c0)' : 'linear-gradient(135deg, #dc004e, #ad0e4e)',
                                  borderRadius: '50%',
                                  p: 0.5,
                                  mr: 2,
                                  minWidth: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <CheckCircleOutlineIcon sx={{ color: 'white', fontSize: '16px' }} />
                              </Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {benefit}
                              </Typography>
                            </Box>
                          </Fade>
                        ))}
                      </Box>

                      {/* Audience-specific CTA */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Button
                          variant="contained"
                          size="large"
                          component={RouterLink}
                          to="/register"
                          sx={{
                            borderRadius: '12px',
                            px: 4,
                            background: viewMode === 'brand' ? 'linear-gradient(135deg, #ff6b6b, #ffa726)' : 'linear-gradient(135deg, #4ecdc4, #74b9ff)',
                            '&:hover': {
                              background: viewMode === 'brand' ? 'linear-gradient(135deg, #ff5252, #ff9800)' : 'linear-gradient(135deg, #00b894, #0984e3)'
                            }
                          }}
                        >
                          {features[viewMode]?.ctaPrimary}
                        </Button>
                        <Button
                          variant="outlined"
                          size="large"
                          component={RouterLink}
                          to="/how-it-works"
                          sx={{
                            borderRadius: '12px',
                            px: 4,
                            borderColor: viewMode === 'brand' ? '#ff6b6b' : '#4ecdc4',
                            color: viewMode === 'brand' ? '#ff6b6b' : '#4ecdc4',
                            '&:hover': {
                              borderColor: viewMode === 'brand' ? '#ff5252' : '#00b894',
                              color: viewMode === 'brand' ? '#ff5252' : '#00b894',
                              backgroundColor: viewMode === 'brand' ? 'rgba(255, 107, 107, 0.1)' : 'rgba(78, 205, 196, 0.1)'
                            }
                          }}
                        >
                          {features[viewMode]?.ctaSecondary}
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>

          {/* Show general CTA only for 'all' view */}
          {viewMode === 'all' && (
            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                component={RouterLink}
                to="/register"
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 4, py: 1.5, borderRadius: '12px' }}
              >
                Get Started Today
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* Enhanced Stats Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="lg">
          <Typography
            component="h2"
            variant="h3"
            align="center"
            color="text.primary"
            gutterBottom
            sx={{ mb: 2, fontWeight: 600 }}
          >
            {viewMode === 'brand' ? 'Join 500+ Successful Brands' :
             viewMode === 'creator' ? 'Join 2,000+ Earning Creators' :
             'Trusted by Brands and Creators Worldwide'}
          </Typography>

          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: '600px', mx: 'auto' }}
          >
            {viewMode === 'brand' ? 'See why leading brands choose InfHire for their influencer marketing campaigns' :
             viewMode === 'creator' ? 'Join thousands of creators already earning consistent income through InfHire' :
             'Real results from real partnerships'}
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {(viewMode === 'all' ? [
              { number: '500+', label: 'Brands', sublabel: 'trust our platform', icon: <GroupsIcon /> },
              { number: '2,000+', label: 'Creators', sublabel: 'earning monthly', icon: <CreatorEconomyIcon /> },
              { number: '5,000+', label: 'Campaigns', sublabel: 'completed successfully', icon: <AnalyticsIcon /> },
              { number: '98%', label: 'Satisfaction', sublabel: 'rate across all users', icon: <VerifiedIcon /> }
            ] : features[viewMode]?.stats || []).map((stat, index) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <Fade in timeout={800} style={{ transitionDelay: `${index * 150}ms` }}>
                  <MetricCard>
                    {stat.icon && (
                      <Box sx={{
                        color: viewMode === 'creator' ? '#4ecdc4' : '#ff6b6b',
                        mb: 2
                      }}>
                        {stat.icon}
                      </Box>
                    )}
                    <Typography
                      variant="h3"
                      sx={{
                        color: viewMode === 'creator' ? '#4ecdc4' : '#ff6b6b',
                        fontWeight: 700,
                        mb: 1
                      }}
                    >
                      {stat.number}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {stat.label}
                    </Typography>
                    {stat.sublabel && (
                      <Typography variant="body2" color="text.secondary">
                        {stat.sublabel}
                      </Typography>
                    )}
                  </MetricCard>
                </Fade>
              </Grid>
            ))}
          </Grid>

          {/* Success Stories Section */}
          {viewMode === 'all' && (
            <Box sx={{ mt: 8 }}>
              <Typography
                variant="h4"
                align="center"
                sx={{ fontWeight: 600, mb: 6 }}
              >
                Success Stories
              </Typography>

              <Grid container spacing={4}>
                {successStories.map((story, index) => (
                  <Grid item xs={12} md={6} key={story.name}>
                    <Fade in timeout={800} style={{ transitionDelay: `${index * 200}ms` }}>
                      <Card
                        sx={{
                          p: 4,
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '20px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Avatar
                            src={story.avatar}
                            sx={{ width: 56, height: 56, mr: 2 }}
                          />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {story.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color={story.type === 'brand' ? 'primary.main' : 'secondary.main'}
                              sx={{ fontWeight: 600 }}
                            >
                              {story.result}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body1" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
                          "{story.quote}"
                        </Typography>
                        <Chip
                          label={story.type === 'brand' ? 'Brand Partner' : 'Creator Success'}
                          size="small"
                          color={story.type === 'brand' ? 'primary' : 'secondary'}
                          sx={{ mt: 2 }}
                        />
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Container>
      </Box>

      {/* Enhanced CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              background: viewMode === 'brand'
                ? 'linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%)'
                : viewMode === 'creator'
                ? 'linear-gradient(135deg, #4ecdc4 0%, #74b9ff 100%)'
                : 'linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 100%)',
              color: 'white',
              p: { xs: 4, md: 6 },
              borderRadius: '24px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
            }}
          >
            {/* Background decoration */}
            <Box
              sx={{
                position: 'absolute',
                top: '-50%',
                right: '-20%',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                opacity: 0.5
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '-30%',
                left: '-10%',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                opacity: 0.3
              }}
            />

            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700, position: 'relative', zIndex: 1 }}>
              {viewMode === 'brand'
                ? 'Ready to Stop Wasting Your Marketing Budget?'
                : viewMode === 'creator'
                ? 'Ready to Turn Your Creativity Into Real Income?'
                : 'Ready to Connect & Collab?'
              }
            </Typography>

            <Typography variant="h6" paragraph sx={{ mb: 4, opacity: 0.9, position: 'relative', zIndex: 1 }}>
              {viewMode === 'brand'
                ? 'Join 500+ smart brands who stopped chasing vanity metrics and started tracking real sales and growth.'
                : viewMode === 'creator'
                ? 'Join 2,000+ creators who said goodbye to "exposure" payments and hello to guaranteed income.'
                : 'No more fake followers. No more unpaid work. Just authentic partnerships that actually work.'
              }
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to="/register"
                sx={{
                  px: 4,
                  py: 1.5,
                  bgcolor: 'white',
                  color: viewMode === 'brand' ? '#ff6b6b' : viewMode === 'creator' ? '#4ecdc4' : '#ff6b6b',
                  fontWeight: 600,
                  borderRadius: '12px',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                {viewMode === 'brand'
                  ? 'Stop Wasting Money on Fake Followers'
                  : viewMode === 'creator'
                  ? 'Start Getting Paid What You\'re Worth'
                  : 'Join the Real Creator Economy'
                }
              </Button>

              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/how-it-works"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: '12px',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Learn How It Works
              </Button>
            </Stack>

            {/* Trust badge */}
            <Box sx={{ mt: 4, position: 'relative', zIndex: 1 }}>
              <Chip
                label={viewMode === 'brand'
                  ? '✓ Zero Fake Followers Guarantee'
                  : viewMode === 'creator'
                  ? '✓ No "Exposure" Payments - Real Money Only'
                  : '✓ Authentic Partnerships Only'
                }
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: '20px'
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Home;
